import React, { useState } from "react"
import {
    Box,
    BoxProps,
    Flex,
    Heading,
    Icon,
    Text,
    useColorMode,
} from "@chakra-ui/react"
import { format } from "date-fns"
import { Chart, AxisOptions, UserSerie } from "react-charts"
import { linearRegression, linearRegressionLine } from "simple-statistics"

import { LockType, TeamType } from "../../utils/lock-picking/types"
import { ChevronDownIcon, ChevronUpIcon } from "@chakra-ui/icons"

const MatchupItem: React.FC<{ matchup: LockType }> = ({ matchup }) => {
    const { colorMode } = useColorMode()

    const [isOpen, setIsOpen] = useState(false)

    const homeResults = matchup.home.results.filter(
        r =>
            r.gameDateUnix >
            matchup.gameTime.getTime() - 1000 * 60 * 60 * 24 * 160
    )

    const awayResults = matchup.away.results.filter(
        r =>
            r.gameDateUnix >
            matchup.gameTime.getTime() - 1000 * 60 * 60 * 24 * 160
    )

    const pickLinReg = linearRegression(
        homeResults
            .sort(
                (a, b) =>
                    new Date(a.gameDateUnix).getTime() -
                    new Date(b.gameDateUnix).getTime()
            )
            .map((result, index) => [index, parseFloat(result.atsMargin)])
    )

    const pickLinRegLine = linearRegressionLine(pickLinReg)

    const pickLinRegData: TeamType["results"][0][] = [
        ...homeResults.map((result, index) => ({
            ...result,
            atsMargin: String(pickLinRegLine(index)),
        })),
    ]

    const fadeLinReg = linearRegression(
        awayResults
            .sort(
                (a, b) =>
                    new Date(a.gameDateUnix).getTime() -
                    new Date(b.gameDateUnix).getTime()
            )
            .map((result, index) => [index, parseFloat(result.atsMargin)])
    )

    const fadeLinRegLine = linearRegressionLine(fadeLinReg)

    const fadeLinRegData: TeamType["results"][0][] = [
        ...awayResults.map((result, index) => ({
            ...result,
            atsMargin: String(fadeLinRegLine(index)),
        })),
    ]

    const pickData: UserSerie<TeamType["results"][0]>[] = [
        {
            label: "Pick",
            data: [...homeResults].sort(
                (a, b) => a.gameDateUnix - b.gameDateUnix
            ),
        },
        {
            label: "Pick ATS Avg",
            data: pickLinRegData,
        },
    ]

    const fadeData = [
        {
            label: "Fade",
            data: [...awayResults].sort(
                (a, b) => a.gameDateUnix - b.gameDateUnix
            ),
        },
        {
            label: "Fade ATS Avg",
            data: fadeLinRegData,
        },
    ]

    const primaryAxis = React.useMemo(
        (): AxisOptions<TeamType["results"][0]> => ({
            getValue: datum =>
                datum.opponentName +
                " " +
                format(new Date(datum.gameDateUnix), "MM/dd"),
            // elementType: "line",
            // formatters: {
            //     scale: (value: number) => format(new Date(value), "MM/dd"),
            // },
        }),
        []
    )

    const secondaryAxes = React.useMemo(
        (): AxisOptions<TeamType["results"][0]>[] => [
            {
                getValue: datum => parseFloat(datum.atsMargin),
                elementType: "line",
                scaleType: "linear",
                // min: -30,
                // max: 30,
            },
        ],
        []
    )

    const [leftSide, rightSide] = [
        {
            ...matchup.home,
            atsMargin: matchup.atsMargin.home,
            winPercent: matchup.winPercent.home,
            weightedAtsMargin: matchup.weightedAtsMargin.home,
        },
        {
            ...matchup.away,
            atsMargin: matchup.atsMargin.away,
            winPercent: matchup.winPercent.away,
            weightedAtsMargin: matchup.weightedAtsMargin.away,
        },
    ].sort((a, b) => b.winPercent - a.winPercent)

    return (
        <Box
            w="100%"
            h={isOpen ? "472px" : "180px"}
            overflow="hidden"
            p={4}
            maxW="400px"
            minW="330px"
            borderRadius="md"
            boxShadow="0 2px 6px rgba(125, 125, 125, 0.2)"
            mb={4}
            transition="0.2s ease-in-out"
            onClick={e => {
                if (isOpen) return
                e.preventDefault()
                e.stopPropagation()
                setIsOpen(!isOpen)
            }}
            _hover={{
                cursor: "pointer",
            }}
            zIndex={1}
        >
            <Flex
                flex={1}
                justify="space-between"
                pos="relative"
                onClick={e => {
                    e.preventDefault()
                    e.stopPropagation()
                    setIsOpen(!isOpen)
                }}
            >
                <Box>
                    <Heading fontSize="sm">{leftSide.team}</Heading>
                    <DetailText label="ATS Record:" value={leftSide.record} />
                    <DetailText
                        label="ATS +/-:"
                        value={leftSide.atsMargin.toFixed(1)}
                    />
                    <DetailText
                        label="Wt'd ATS +/-:"
                        value={leftSide.weightedAtsMargin.toFixed(1)}
                    />
                </Box>
                <Flex pos="absolute" w="100%" align="center" flexDir="column">
                    <Text color="gray.400" fontSize="xs" mb={1}>
                        Win % Differential
                    </Text>
                    <Heading fontSize="md">
                        {colorMessage(
                            Math.abs(
                                matchup.winPercent.home -
                                    matchup.winPercent.away
                            ).toFixed(1)
                        )}
                    </Heading>
                    <Text color="gray.400" fontSize="xs" mb={1}>
                        +/- Differential
                    </Text>
                    <Flex>
                        <Heading fontSize="md">
                            {Math.abs(
                                matchup.atsMargin.home - matchup.atsMargin.away
                            ).toFixed(1)}
                        </Heading>
                        <Text color="gray.400" fontSize="sm" ml={1}>
                            (wtd:&nbsp;
                            {Math.abs(
                                matchup.weightedAtsMargin.home -
                                    matchup.weightedAtsMargin.away
                            ).toFixed(1)}
                            )
                        </Text>
                        <Text color="gray.400" fontSize="sm" ml={1}>
                            (abs:&nbsp;
                            {(
                                Math.abs(matchup.atsMargin.home) +
                                Math.abs(matchup.atsMargin.away) / 2
                            ).toFixed(1)}
                            )
                        </Text>
                    </Flex>
                    <Text size="sm" color="gray.400">
                        {matchup.timeWithZone ||
                            format(matchup.gameTime, "h:mma")}{" "}
                        |{format(matchup.gameTime, " MMM dd, yyyy")}
                    </Text>
                </Flex>
                <Box textAlign="right">
                    <Heading fontSize="sm">{rightSide.team}</Heading>
                    <DetailText
                        label="ATS Record:"
                        value={rightSide.record}
                        alignRight
                    />
                    <DetailText
                        label="ATS +/-:"
                        value={rightSide.atsMargin.toFixed(1)}
                        alignRight
                    />
                    <DetailText
                        label="Wt'd ATS +/-:"
                        value={rightSide.weightedAtsMargin.toFixed(1)}
                    />
                </Box>
                <Icon
                    boxSize="24px"
                    pos="absolute"
                    bottom="-56px"
                    right="24px"
                    as={isOpen ? ChevronUpIcon : ChevronDownIcon}
                />
            </Flex>
            {isOpen && (
                <Flex
                    flexDir="column"
                    w="100%"
                    pos="relative"
                    top="40px"
                    py={4}
                >
                    <Text>{matchup.home.team}</Text>
                    <Box h={32} mb={4}>
                        <Chart
                            options={{
                                data: pickData,
                                primaryAxis,
                                secondaryAxes,
                                dark: colorMode === "dark",
                            }}
                        />
                    </Box>
                    <Box h={32}>
                        <Text>{matchup.away.team}</Text>
                        <Chart
                            options={{
                                data: fadeData,
                                primaryAxis,
                                secondaryAxes,
                                dark: colorMode === "dark",
                            }}
                        />
                    </Box>
                    {/* Pick:
                    {matchup.pick.results?.filter(Boolean).map(m => (
                        <Text>
                            Week {m?.week}: {m?.atsMargin}
                        </Text>
                    ))}
                    Fade:
                    {matchup.fade.results?.filter(Boolean).map(m => (
                        <Text>
                            Week {m?.week}: {m?.atsMargin}
                        </Text>
                    ))} */}
                </Flex>
            )}
        </Box>
    )
}

export default MatchupItem

const DetailText: React.FC<{
    label: string
    value: string
    alignRight?: boolean
}> = ({ label, value, alignRight }) => (
    <Flex align="center" justify={alignRight ? "flex-end" : "flex-start"}>
        <Text
            color="gray.400"
            fontSize="xs"
            fontStyle="italic"
            pr={1}
            textAlign="right"
        >
            {label}{" "}
        </Text>
        <Text color="gray.400">{value}</Text>
    </Flex>
)

function colorMessage(differential: string) {
    const diff = parseFloat(differential)
    if (diff < 25)
        return (
            <Flex color="rgb(228, 9, 9)" flexDir="column" align="center">
                <Text>
                    <NumberBox bg="rgb(228, 9, 9)">{differential}</NumberBox>
                </Text>
                <Box fontSize="sm">(Cringe)</Box>
            </Flex>
        )

    if (diff < 37)
        return (
            <Flex color="rgb(249, 183, 24)" flexDir="column" align="center">
                <Text>
                    <NumberBox bg="rgb(249, 183, 24)">{differential}</NumberBox>
                </Text>
                <Box fontSize="sm">(Decent)</Box>
            </Flex>
        )

    if (diff < 50)
        return (
            <Flex color="rgb(145, 179, 8)" flexDir="column" align="center">
                <Text>
                    <NumberBox bg="rgb(145, 179, 8)">{differential}</NumberBox>
                </Text>
                <Box fontSize="sm">(Good)</Box>
            </Flex>
        )

    if (diff < 55)
        return (
            <Flex color="rgb(81, 130, 2)" flexDir="column" align="center">
                <Text>
                    <NumberBox bg="rgb(81, 130, 2)">{differential}</NumberBox>
                </Text>
                <Box fontSize="sm">(Great)</Box>
            </Flex>
        )

    return (
        <Flex color="magenta" flexDir="column" align="center">
            <Text>
                <NumberBox bg="magenta">{differential}</NumberBox>
            </Text>
            <Box fontSize="sm">(Masterlock)</Box>
        </Flex>
    )
}

const NumberBox: React.FC<BoxProps> = ({ children, ...props }) => (
    <Box
        as="span"
        color="white"
        px={1}
        borderRadius="6px"
        fontWeight="bold"
        {...props}
    >
        {children}
    </Box>
)
